import { Button } from '@/components/ui/Button';
import { Edit, Mail, MoreVertical, Signature, Trash2, Unlink } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDisconnectOAuth, useSetDefaultSender } from '../hooks/useSenders';
import { useSendersStore } from '../store/senders.store';
import { Sender } from '../types/sender.types';

interface SenderCardProps {
  sender: Sender;
  onEdit?: (sender: Sender) => void;
  onValidate?: (sender: Sender) => void;
}

export const SenderCard = ({ sender, onEdit }: SenderCardProps) => {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const { openEditModal, openDeleteModal } =
    useSendersStore();

  const setDefaultMutation = useSetDefaultSender();
  const disconnectOAuthMutation = useDisconnectOAuth();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleEdit = () => {
    setShowMenu(false);
    if (onEdit) {
      onEdit(sender);
    } else {
      openEditModal(sender.uuid);
    }
  };

  // Validation logic removed as per user request

  const handleDelete = () => {
    setShowMenu(false);
    openDeleteModal(sender.uuid);
  };

  const handleSetDefault = () => {
    setShowMenu(false);
    setDefaultMutation.mutate(sender.uuid);
  };

  const handleDisconnectOAuth = () => {
    setShowMenu(false);
    disconnectOAuthMutation.mutate(sender.uuid);
  };

  return (
    <div className="group relative rounded-2xl border border-neutral-200 bg-white p-4 transition-all duration-200 hover:border-neutral-300 hover:shadow-lg sm:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <div className="mb-3 flex items-start gap-3">
            <div className="bg-primary-50 group-hover:bg-primary-100 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl transition-colors">
              <Mail className="text-primary-600 h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                <h3 className="group-hover:text-primary-700 truncate font-bold text-neutral-900 transition-colors">
                  {sender.name}
                </h3>

                {/* Status Badges */}
                <div className="flex items-center gap-1.5">
                  {sender.isDefault && (
                    <span className="bg-primary-100 text-primary-800 border-primary-200 inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium">
                      {t('senders.status.default', 'Default')}
                    </span>
                  )}
                  {sender.oauthConnected && (
                    <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-800">
                      Microsoft 365
                    </span>
                  )}
                  {!sender.isActive && (
                    <span className="inline-flex items-center rounded-full border border-neutral-200 bg-neutral-100 px-2.5 py-1 text-xs font-medium text-neutral-800">
                      {t('senders.status.inactive', 'Inactive')}
                    </span>
                  )}
                </div>

                {sender.useSignature && (
                  <span className="text-primary-700 bg-primary-50 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium">
                    <Signature className="h-3 w-3" />
                    {t('senders.form.signature', 'HTML Signature')}
                  </span>
                )}
              </div>
              <p className="mb-4 line-clamp-2 text-sm text-neutral-600">
                {sender.email}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
            <div>
              <p className="font-medium text-neutral-500">
                {sender.oauthConnected
                  ? t('senders.oauthAccount', 'Compte Microsoft')
                  : t('senders.domain', 'Domain')}
              </p>
              <p className="mt-1 text-sm font-semibold text-neutral-900">
                {sender.oauthConnected
                  ? sender.oauthEmail || '-'
                  : sender.metadata.domain || '-'}
              </p>
            </div>
            <div>
              <p className="font-medium text-neutral-500">
                {t('senders.lastUsed', 'Last Used')}
              </p>
              <p className="mt-1 text-sm font-semibold text-neutral-900">
                {sender.metadata.lastUsed
                  ? formatDate(sender.metadata.lastUsed)
                  : '-'}
              </p>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <p className="font-medium text-neutral-500">
                {t('senders.created', 'Created')}
              </p>
              <p className="mt-1 text-sm font-semibold text-neutral-900">
                {formatDate(sender.createdAt)}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-row items-center gap-2 lg:ml-6 lg:flex-col lg:items-end lg:gap-3">
          {/* Quick actions */}
          <div className="flex items-center gap-2">
            {!sender.isDefault && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSetDefault}
                disabled={setDefaultMutation.isPending}
              >
                {t('senders.setDefault', 'Set Default')}
              </Button>
            )}
          </div>

          {/* Menu */}
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="inline-flex items-center justify-center rounded-xl bg-neutral-50 p-2.5 text-neutral-600 transition-colors hover:bg-neutral-100"
            >
              <MoreVertical className="h-4 w-4" />
            </button>

            {showMenu && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                />

                {/* Menu */}
                <div className="absolute top-full right-0 z-20 mt-2 w-48 rounded-xl border border-neutral-200 bg-white shadow-lg">
                  <div className="p-2">
                    <button
                      onClick={handleEdit}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
                    >
                      <Edit className="h-4 w-4" />
                      {t('senders.edit', 'Edit')}
                    </button>

                    {sender.oauthConnected && (
                      <button
                        onClick={handleDisconnectOAuth}
                        className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium text-orange-700 transition-colors hover:bg-orange-50"
                      >
                        <Unlink className="h-4 w-4" />
                        {t('senders.disconnect', 'Deconnecter OAuth')}
                      </button>
                    )}

                    <button
                      onClick={handleDelete}
                      className="text-error-700 hover:bg-error-50 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                      {t('senders.delete.title', 'Delete')}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
